import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionThreeImagesVerticalApi from "hooks/api/private/blocks/useSectionThreeImagesVerticalApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionThreeImagesVerticalBody,
    SectionThreeImagesVerticalDetails,
} from "types/api/blocks/sectionThreeImagesVertical";
import { DesktopMobileForm } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type TwoImageTextureForm = {
    id: string | undefined;
    left_img_id: string | undefined;
    left_img: DesktopMobileForm;
    right_img_id: string | undefined;
    right_img: DesktopMobileForm;
    center_img_id: string | undefined;
    center_img: DesktopMobileForm;
};

type SectionThreeImagesVerticalType = {
    setTextureImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<TwoImageTextureForm>;
};

function useSectionThreeImagesVertical(): SectionThreeImagesVerticalType {
    const {
        saveSectionThreeImagesVertical,
        getOneSectionThreeImagesVertical,
        deleteSectionThreeImagesVertical,
    } = useSectionThreeImagesVerticalApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                left_img_id: {
                    type: "simple",
                    validators: [],
                },
                right_img_id: {
                    type: "simple",
                    validators: [],
                },
                center_img_id: {
                    type: "simple",
                    validators: [],
                },
                left_img: notEmptyMedia,
                right_img: notEmptyMedia,
                center_img: notEmptyMedia,
            },
        };
    }, []);

    const initialValues: TwoImageTextureForm = React.useMemo(() => {
        return {
            id: undefined,
            left_img_id: undefined,
            left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            right_img_id: undefined,
            right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            center_img_id: undefined,
            center_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
        };
    }, []);

    const formHook = useForm<TwoImageTextureForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "left_img",
    });

    const { setDesktopMobileImage: setRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "right_img",
    });

    const { setDesktopMobileImage: setTextureImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "center_img",
    });

    const [twoImagesTextureDetails, setTwoImageTextureDetails] = React.useState<
        SectionThreeImagesVerticalDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionThreeImagesVertical(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setTwoImageTextureDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setTwoImageTextureDetails, getOneSectionThreeImagesVertical, blockGroupCompositionId]);

    React.useEffect(() => {
        if (twoImagesTextureDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = twoImagesTextureDetails.id;
                newForm.left_img_id = twoImagesTextureDetails.left_img_id;
                newForm.right_img_id = twoImagesTextureDetails.right_img_id;
                newForm.center_img_id = twoImagesTextureDetails.center_img_id;
                newForm.left_img = {
                    id: twoImagesTextureDetails.left_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.left_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.left_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.left_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.left_img?.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.right_img = {
                    id: twoImagesTextureDetails.right_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.right_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.right_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.right_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.right_img?.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.center_img = {
                    id: twoImagesTextureDetails.center_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.center_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.center_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.center_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.center_img?.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, twoImagesTextureDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const twoImagesTextureBody: SectionThreeImagesVerticalBody = {
                    id: blockGroupCompositionId,
                    left_img: {
                        id: form.left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.left_img.mobile),
                    },
                    right_img: {
                        id: form.right_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.right_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.right_img.mobile),
                    },
                    center_img: {
                        id: form.center_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.center_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.center_img.mobile),
                    },
                };
                const promises: Promise<unknown>[] = [];

                const [apiCall] = saveSectionThreeImagesVertical(twoImagesTextureBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionThreeImagesVertical,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionThreeImagesVerticalType => ({
            buttonDelete: deleteSectionThreeImagesVertical,
            setTextureImage,
            setRightImage,
            setLeftImage,
            formHook,
        }),
        [deleteSectionThreeImagesVertical, setTextureImage, setRightImage, setLeftImage, formHook]
    );
}

export default useSectionThreeImagesVertical;
